import React, { forwardRef, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CaretDown, Check } from 'phosphor-react';
import { cn } from './cn';

const FIELD = 'w-full rounded-2xl bg-cream-200/60 px-4 py-3 text-espresso placeholder:text-espresso-soft/50 ' +
    'ring-1 ring-espresso/10 transition-all duration-300 ease-fluid focus:bg-cream-50 focus:ring-2 focus:ring-sage/60';

function Label({ children }) {
    return <span className="mb-1.5 block text-sm font-medium text-espresso-soft">{children}</span>;
}
function ErrorOrHint({ error, hint }) {
    if (error) return <span className="mt-1 block text-xs text-danger">{error}</span>;
    if (hint) return <span className="mt-1 block text-xs text-espresso-soft/70">{hint}</span>;
    return null;
}

export const Input = forwardRef(function Input({ label, error, hint, className, ...rest }, ref) {
    return (
        <label className="block">
            {label && <Label>{label}</Label>}
            <input ref={ref} className={cn(FIELD, 'tnum', error && 'ring-danger/50 focus:ring-danger/60', className)} {...rest} />
            <ErrorOrHint error={error} hint={hint} />
        </label>
    );
});

export const Textarea = forwardRef(function Textarea({ label, error, hint, className, rows = 3, ...rest }, ref) {
    return (
        <label className="block">
            {label && <Label>{label}</Label>}
            <textarea ref={ref} rows={rows} className={cn(FIELD, 'resize-none', error && 'ring-danger/50', className)} {...rest} />
            <ErrorOrHint error={error} hint={hint} />
        </label>
    );
});

// Custom Listbox select — no native chrome. options: [{ value, label }].
export function Select({ label, value, onChange, options = [], placeholder = 'Chọn…', error, hint, className, disabled }) {
    const selected = options.find(o => String(o.value) === String(value));
    return (
        <div className={cn('block', className)}>
            {label && <Label>{label}</Label>}
            <Listbox value={value} onChange={onChange} disabled={disabled}>
                <div className="relative">
                    <Listbox.Button className={cn(FIELD, 'flex items-center justify-between text-left disabled:opacity-50', error && 'ring-danger/50')}>
                        <span className={cn(!selected && 'text-espresso-soft/50')}>{selected ? selected.label : placeholder}</span>
                        <CaretDown size={16} weight="light" className="ml-2 shrink-0 text-espresso-soft" />
                    </Listbox.Button>
                    <Transition as={Fragment}
                        enter="transition duration-200 ease-fluid" enterFrom="opacity-0 -translate-y-1" enterTo="opacity-100 translate-y-0"
                        leave="transition duration-150 ease-fluid" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Listbox.Options className="absolute z-30 mt-2 max-h-64 w-full overflow-auto rounded-2xl bg-cream-50 p-1.5 shadow-ambient-lg ring-1 ring-espresso/10 focus:outline-none">
                            {options.map(o => (
                                <Listbox.Option key={String(o.value)} value={o.value}
                                    className={({ active }) => cn('flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-sm transition-colors', active && 'bg-espresso/[0.06]')}>
                                    {({ selected: sel }) => (
                                        <>
                                            <span className={cn(sel && 'font-semibold')}>{o.label}</span>
                                            {sel && <Check size={16} weight="bold" className="text-sage-deep" />}
                                        </>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </Transition>
                </div>
            </Listbox>
            <ErrorOrHint error={error} hint={hint} />
        </div>
    );
}
